import {
  Form,
  Forms,
  RadioButton,
  RadioGroup,
  useFormsContext,
} from '@ally/forms'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Label,
  TextBody,
  useModalContext,
  useTriggerSnackbar,
} from '@ally/metronome-ui'
import { Private } from '@ally/private'
import allytm from '@ally/allytm'
import { FundsAllocationFields } from '../FundsAllocationFields'
import {
  AddBenePayload,
  AddExistingBenePayloadObject,
  Beneficiary,
  IraPlan,
  AccountDetails,
  AddBenePayloadObject,
} from '../../../types'
import { useBeneficiariesApi } from '../../../api/beneficiaries-api'
import { useNonIraBeneficiaries } from '../../../services/NonIraBeneficiariesProvider'
import { AddNewBene } from '.'
import { fireErrorTagging, KeyValuePairObj } from '../../../utils/tagging'
import { getErrorMessage, removeLeadingZeros } from '../../../utils/formatter'
import { MAX_NON_IRA_BENES } from '../../../utils/constants'

const defaultErrorMessage =
  "It's us - we're having a moment. We couldn't add your beneficiary. We'll be quick about it, so try again in a bit."

export const addExistingBeneErrors: {
  [key: string]: {
    errorMessage: string
    fieldErrorMessage: string
    field: string
  }
} = {
  USB_BUS_633: {
    errorMessage: `You can't add the same beneficiary twice. Call us at 1-877-247-2559 if you have questions about how to add your beneficiaries.`,
    fieldErrorMessage: '',
    field: '',
  },
}

export interface FormValues {
  beneficiarySelection: Beneficiary
  grantorsSameAsOwners: string
  reallocationMethod: string
  reallocationPercentages: number | null
  newBeneficiaryFundAllocation: string
}

interface BeneAllocation {
  beneId: string
  allocation: string | number
}

export const AddExistingBeneInnerForm: React.FC<{
  beneficiarySelection?: Beneficiary
  existingBenes?: Beneficiary[]
  isLoading: boolean
  setAllocationFormValues: React.Dispatch<
    React.SetStateAction<Record<string, string | number>>
  >
  formError: string
  isIraPage: boolean
  account: AccountDetails | IraPlan
  isMarried?: string
}> = ({
  beneficiarySelection,
  existingBenes,
  isLoading,
  setAllocationFormValues,
  formError,
  isIraPage,
  account,
  isMarried,
}) => {
  const {
    formId,
    values,
    setFieldValue,
    handleSubmit,
    setFormError,
    errors,
    isSubmitting,
  } = useFormsContext()
  const { setOpen } = useModalContext()

  const beneAllocations = useMemo(() => {
    return (
      existingBenes &&
      existingBenes.map(bene => {
        const allocation: BeneAllocation = {
          beneId: bene.beneficiaryId,
          allocation:
            bene.allocationPercentage && bene.allocationPercentage !== 'Equal'
              ? // istanbul ignore next
                parseInt(bene.allocationPercentage, 10).toString()
              : '0',
        }
        return allocation
      })
    )
  }, [existingBenes])

  useEffect(() => {
    const newBenePercentage =
      !existingBenes || !existingBenes.length // istanbul ignore next // we already have a test for this
        ? '100'
        : '0'

    // istanbul ignore next optional chaining
    const isEqualDistribution = existingBenes?.find(
      bene => bene.allocationPercentage === 'Equal',
    )

    // istanbul ignore next
    setFieldValue(
      'reallocationMethod',
      isEqualDistribution || existingBenes?.length === 1 ? 'equal' : 'specific',
    )

    // istanbul ignore next optional chaining
    beneAllocations?.forEach(bene => {
      setFieldValue(bene.beneId, bene.allocation)
    })
    setFieldValue('newBeneficiaryFundAllocation', newBenePercentage)
  }, [existingBenes, beneAllocations, setFieldValue])

  const total = Object.keys(values)
    .filter(
      key => key !== 'reallocationMethod' && key !== 'grantorsSameAsOwners',
    )
    .reduce((acc, curr) => {
      if (Number.isNaN(Number(values[curr]))) {
        return acc
      }
      return acc + Number(values[curr])
    }, 0)

  // istanbul ignore next // remove this ignore when we add submit integration and tests
  const handleInputChange = (name: string, value: string): void => {
    setFieldValue(name, value)
    setAllocationFormValues(prevState => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleCancel = (): void => {
    setOpen(false)
  }

  const onSubmit = (): void => {
    fireErrorTagging(errors as KeyValuePairObj)
    if (values.reallocationMethod !== 'specific' || total === 100) {
      setFormError('')
      handleSubmit()
    }
  }

  // if we are inside add existing ira bene we have a stepper with the same logic as add new bene but with some prefilled fields
  if (isIraPage)
    return (
      <Form id={formId}>
        <AddNewBene
          isIraPage={isIraPage}
          existingBenes={existingBenes}
          distributionType={account.distributionType}
          accountId={account.planId as string}
          creatingExistingBene
          beneficiarySelection={beneficiarySelection}
          isMarried={isMarried}
        />
      </Form>
    )

  return (
    <Form id={formId}>
      {formError && (
        <Box mb="md">
          <Alert
            variant="error"
            disableContainer
            body={formError}
            focusOnMount
          />
        </Box>
      )}
      {beneficiarySelection &&
        beneficiarySelection.beneficiaryClassificationCode === 'T' && (
          <Box>
            <Label htmlFor="grantorsSameAsOwners">
              Are the grantors of this trust the same as the owners of this Ally
              account?
            </Label>
            <RadioGroup
              name="grantorsSameAsOwners"
              legendText=""
              isLegendHidden
              defaultValue="Yes"
            >
              <RadioButton label="Yes" value="Yes" />
              <RadioButton label="No" value="No" />
            </RadioGroup>
          </Box>
        )}
      {existingBenes && existingBenes.length > 0 ? (
        <>
          <Label htmlFor="reallocationMethod">
            How do you want to reallocate your money?
          </Label>
          <RadioGroup name="reallocationMethod" legendText="" isLegendHidden>
            <RadioButton
              label="Equally"
              value="equal"
              data-testid="allocate-funds-equally"
            />
            <RadioButton
              label="Specific percentage"
              value="specific"
              data-testid="allocate-funds-specifically"
            />
          </RadioGroup>
          {values.reallocationMethod === 'specific' && (
            <Private>
              <FundsAllocationFields
                existingBeneficiaries={existingBenes}
                total={total}
                setPercentageValue={handleInputChange}
              />
            </Private>
          )}
        </>
      ) : (
        <Box mt="md" mb="md">
          <Label htmlFor="allocationAmount">Allocation amount</Label>
          <TextBody tag="span" size="sm">
            100%
          </TextBody>
        </Box>
      )}
      <ButtonGroup>
        <Button
          allytmln="Submit Add Existing Beneficiary"
          data-testid="submit-existing-button"
          text="Save"
          onClick={onSubmit}
          disabled={isSubmitting}
          loading={isLoading}
        />
        <Button
          allytmln="Cancel Add Beneficiary"
          text="Cancel"
          variant="link"
          onClick={handleCancel}
          data-testid="cancel-modal-existing"
        />
      </ButtonGroup>
    </Form>
  )
}

export const AddExistingBene: React.FC<{
  beneficiarySelection?: Beneficiary
  existingBenes?: Beneficiary[]
  isIraPage: boolean
  account: AccountDetails | IraPlan
  distributionType?: string
  isMarried?: string
}> = ({
  beneficiarySelection,
  existingBenes,
  isIraPage,
  account,
  distributionType,
  isMarried,
}) => {
  const { createBeneficiary } = useBeneficiariesApi()
  const { getBeneficiaries } = useNonIraBeneficiaries()
  const { triggerSnackbar } = useTriggerSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const [formError, setFormError] = useState('')
  const [allocationFormValues, setAllocationFormValues] = useState<
    Record<string, string | number>
  >({})

  const newBeneficiaryPayload = (
    values: Partial<FormValues>,
  ): AddExistingBenePayloadObject | AddBenePayloadObject => {
    if (
      beneficiarySelection &&
      beneficiarySelection.copyBeneficiaryType === 'IRA'
    ) {
      return {
        firstName: beneficiarySelection.firstName,
        lastName: beneficiarySelection.lastName,
        dateOfBirth: dayjs(beneficiarySelection.dateOfBirth)
          .format('YYYY-MM-DD')
          .toString(),
        taxId: beneficiarySelection.ssn,
        beneficiaryClassificationCode: 'I',
        address: beneficiarySelection.address,
        allocationPercentage: removeLeadingZeros(
          values.newBeneficiaryFundAllocation,
        ),
      }
    }
    return {
      allocationPercentage: removeLeadingZeros(
        values.newBeneficiaryFundAllocation,
      ),
      beneficiaryId: beneficiarySelection?.beneficiaryId as string,
      copyBeneficiaryType: beneficiarySelection?.copyBeneficiaryType as string,
    }
  }

  const createPayloadObject = (values: Partial<FormValues>): AddBenePayload => {
    // there are two different scenarios, one where beneficiaries have specific allocation percentages
    // in which we have to pass all beneficiaries with bene id and allocation percentage.
    // the other scenario would be equal allocation for which we only need to pass the new bene.
    const newBeneficiary = newBeneficiaryPayload(values)
    if (values.reallocationMethod === 'equal') {
      return {
        beneficiaries: [{ ...newBeneficiary, allocationPercentage: 'Equal' }],
        distributionType,
      }
    }

    // customized objects for each bene with allocation percentage and id.
    const allBenePayload = existingBenes
      ? existingBenes.map(bene => ({
          beneficiaryId: bene.beneficiaryId,
          allocationPercentage: removeLeadingZeros(
            values[bene.beneficiaryId as keyof FormValues] as string,
          ),
        }))
      : // istanbul ignore next // low value
        []

    return {
      beneficiaries: [newBeneficiary, ...allBenePayload],
      distributionType,
    }
  }

  const handleSubmit = async (values: Partial<FormValues>): Promise<void> => {
    const payload = createPayloadObject(values)
    try {
      setIsLoading(true)
      await createBeneficiary(account.accountId, payload)
      await getBeneficiaries()
      // istanbul ignore next
      triggerSnackbar({
        text: 'You updated your beneficiaries.',
        columnSize: 4,
        snackbarButtonText: 'Close',
        onClose: () =>
          requestAnimationFrame(() => {
            // This if condition bring focus on max bene warning message
            if (existingBenes?.length === MAX_NON_IRA_BENES - 1) {
              document
                .getElementById(`${account.accountId}-add-bene-success-focus`)
                ?.getElementsByTagName('span')[0]
                .focus()
            } else {
              document
                .getElementById(`${account.accountId}-add-bene-success-focus`)
                ?.focus()
            }
          }),
      })
      // istanbul ignore next - tagging
      allytm.event('customEvent', {
        name: 'formSuccess',
        type: '',
        attr: {
          formValues: {
            form: 'Add Existing Beneficiary',
            values: [],
          },
        },
      })
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      if (errorMessage === 'USB_BUS_633') {
        setFormError(addExistingBeneErrors[errorMessage].errorMessage)
        allytm.event(
          'customError',
          addExistingBeneErrors[errorMessage].errorMessage,
          '',
        )
      } else {
        setFormError(defaultErrorMessage)
        allytm.event(
          'customError',
          'It’s us – we’re having a moment. We couldn’t add your beneficiary. We’ll be quick about it, so try again in a bit.',
          '',
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    allytm.event(
      'modalView',
      isIraPage
        ? 'AOS:Beneficiaries:Bank IRAs:Add Beneficiary:Existing Beneficiary Confirmation'
        : 'AOS:Beneficiaries:Bank Checking Savings and CDs:Add Beneficiary:Existing Beneficiary Confirmation',
      {
        treatAsPageview: true,
      },
    )
  }, [])

  return (
    <Forms
      formId="add-existing-beneficiaries"
      initialValues={allocationFormValues}
      onSubmit={handleSubmit}
    >
      <AddExistingBeneInnerForm
        beneficiarySelection={beneficiarySelection}
        existingBenes={existingBenes}
        isLoading={isLoading}
        setAllocationFormValues={setAllocationFormValues}
        formError={formError}
        isIraPage={isIraPage}
        account={account}
        isMarried={isMarried}
      />
    </Forms>
  )
}
